'use client';
import React from 'react';
import Cookies from 'js-cookie';

import { BlockWrapper } from '@/components/block-wrapper/block-wrapper';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { PageComponentImage } from '@/components/page-components/image/page-component-image';
import { PageComponentMainBottomBorder } from '@/components/page-components/main/page-component-main-bottom-border';
import { PageComponentMainHeader } from '@/components/page-components/main/page-component-main-header';
import TenantDescription from '@/components/page-components/tenant-detail-page/tenant-description';
import ViewOnMap from '@/components/page-components/tenant-detail-page/view-on-map';
import { TenantInfo } from '@/components/page-components/tenant-info/tenant-info';
import { Share } from '@/components/share/share';
import { FavouriteButton } from '@/components/ui/favourite-button/favourite-button';
import LinkButton from '@/components/ui/link-button/link-button';
import { isBookingSubdomain, sortByPriority } from '@/helpers/utils';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { ILibraryServiceType } from '@/lib/fragments/libraryServiceType';
import { IMeetingplacePageHomeMinifiedExtendedOpeningHours } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import Image from 'next/image';
import { useTenantConfig } from '@/providers/ConfigProvider';
import { normalizeTextString } from '@/helpers/normalizeTextString';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import useNewsletterPopupAutomation from '@/hooks/useNewsletterPopupAutomation';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import Button from '@/components/ui/button/button';
import { redirect } from 'next/navigation';
import { useUser } from '@/providers/UserProvider';
import { useProfileSlider } from '@/providers/ProfileSliderProvider';

interface MeetingPlacePageServiceProps {
  contentfulLibraryServiceType: ILibraryServiceType;
  allContentfulMeetingPlacePageServiceItem: {
    nodes: IMeetingPlacePageServiceItem[];
  };
  contentfulMeetingPlacePageHome: IMeetingplacePageHomeMinifiedExtendedOpeningHours;
}

const MeetingPlacePageServiceType = ({ data, metaData, node_locale }: any) => {
  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX;
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const { user } = useUser();
  // const user_token = Cookies.get('user_token');
  const { openSlider } = useProfileSlider();

  const page = data?.libraryServiceType;
  const services = data?.meetingPlacePageServiceItems;
  const homepage = data?.meetingPlacePageHomeCollection;
  const parent = data?.serviceListPage;

  if (parent.slug && prefix) {
    const normalizedSlug = normalizeTextString(parent.slug);
    const locale = page?.sys?.locale;

    const fullPrefix = `/${prefix}/${locale}`;

    if (!normalizedSlug.startsWith(fullPrefix)) {
      parent.slug = `${fullPrefix}/${normalizedSlug}`;
    }
  }

  const pageName = page?.name ? page?.name : '';
  const image = page?.coverImageWrapper?.jsonBynderAsset?.originalUrl;

  const isMobile = useIsMobile();
  const isWebView = useGetMobileAppWebViewHeader();

  let servicesSortedByPriority = [];
  servicesSortedByPriority = services?.sort(sortByPriority);

  const gtmParams: IGtmParameters = {
    event: 'view_service_sub_category_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  useNewsletterPopupAutomation();

  const onBookingEngineButtonClick = (bookingUrl: string) => {
    const url = new URL(bookingUrl);
    const pathWithQuery = url.pathname + url.search;
    if (user) {
      if (bookingUrl) {
        redirect(pathWithQuery + '?isHosted=true');
        // window.open(`${window.location.origin}${pathWithQuery}`, '_blank');
      }
    } else {
      openSlider(pathWithQuery + '?isHosted=true');
    }
  };

  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
      />

      {isMobile ? (
        <>
          <div className="w-full rounded-b-xxl bg-background-primary-light px-6 pb-8">
            <Breadcrumbs
              breadcrumbs={[pageName]}
              homeText={homepage?.breadcrumbHomeText ?? ''}
              cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
              node_locale={node_locale}
              parent={parent}
            />
            <div className="pt-5">
              <PageComponentImage
                imageURL={image}
                imageAlt={page?.coverImageWrapper?.altText}
                hasColoredBackground={true}
                node_locale={node_locale}
              />
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <PageComponentMainHeader header={pageName} />
              </div>
              {page?.description && (
                <TenantDescription
                  description={page?.description}
                  node_locale={page?.node_locale}
                />
              )}
              {homepage?.meetingPlace?.showSocialShare && (
                <Share className="relative top-4 ml-auto" />
              )}
            </div>
          </div>
          <div className="mt-8 px-6">
            {/* Tenant info may be utilized in the future */}
            {/* <TenantInfo
                page={page}
                homepage={homepage}
                node_locale={page.node_locale}
                isMobile={isMobile}
                variant="topInfo"
              /> */}
            {servicesSortedByPriority?.length > 0 &&
              servicesSortedByPriority?.map(
                (service: IMeetingPlacePageServiceItem) => {
                  const serviceItemImage =
                    service?.mediaField?.jsonBynderAsset?.originalUrl || image;
                  const isSvg = serviceItemImage?.endsWith('.svg');
                  const imageSrc =
                    serviceItemImage && !isSvg
                      ? `${serviceItemImage}?fm=avif`
                      : serviceItemImage;
                  return (
                    <>
                      <BlockWrapper className="mt-6 rounded-xl !p-5 lg:py-10 xl:py-12">
                        <div className="mb-1 flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            {imageSrc && (
                              <Image
                                src={imageSrc}
                                alt={
                                  service?.mediaField?.altText ||
                                  page?.coverImageWrapper?.altText ||
                                  'alt text not found'
                                }
                                className="h-16 max-h-16 w-16 max-w-16 object-contain"
                                width={64}
                                height={64}
                              />
                            )}
                            <h4 className="font-title text-[18px] font-bold">
                              {service.name}
                            </h4>
                          </div>
                          <FavouriteButton
                            id={service?.sys?.id ?? ''}
                            type="service"
                            name={service?.name}
                          />
                        </div>
                        {service.description && (
                          <TenantDescription
                            description={service.description}
                            node_locale={service?.sys?.locale}
                          />
                        )}
                        {service?.floorPlanIDs?.length > 0 &&
                          homepage.meetingPlace.visioGlobeMapKey &&
                          homepage.meetingPlace.visioGlobeFloorPlanPage && (
                            <ViewOnMap
                              page={service}
                              homepage={homepage}
                              className="!mb-0 !mt-6 !bg-background-primary"
                            />
                          )}
                        {isLivliFamily &&
                          service.bookingButtonText &&
                          isBookingSubdomain(service?.bookingUrl) && (
                            <Button
                              className={`mb-8 mt-4`}
                              onClick={() => {
                                onBookingEngineButtonClick(service.bookingUrl);
                              }}
                            >
                              {service.bookingButtonText}
                            </Button>
                          )}
                        {(!isLivliFamily ||
                          !isBookingSubdomain(service?.bookingUrl)) &&
                          service.bookingButtonText &&
                          service.bookingUrl && (
                            <LinkButton
                              to={service.bookingUrl}
                              className="mt-6"
                              openInNewTab
                              isExternalLink
                            >
                              {service.bookingButtonText}
                            </LinkButton>
                          )}
                      </BlockWrapper>
                    </>
                  );
                },
              )}

            {/* <div className="-mx-6 mt-10 bg-brand-white px-6 lg:m-0 lg:p-0">
                <TenantInfo
                  page={page}
                  homepage={homepage}
                  node_locale={page.node_locale}
                  isMobile={isMobile}
                  variant="bottomInfo"
                />
              </div> */}
          </div>
        </>
      ) : (
        <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
          <Breadcrumbs
            breadcrumbs={[pageName]}
            homeText={homepage?.breadcrumbHomeText ?? ''}
            cssClass="lg:ml-22"
            node_locale={node_locale}
            parent={parent}
          />
          <div className="grid grid-cols-11 gap-6 pt-5 lg:gap-5">
            <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
              <div className="lg:sticky lg:top-0">
                <PageComponentImage
                  imageURL={image}
                  imageAlt={page?.coverImageWrapper?.altText}
                  hasColoredBackground={true}
                  node_locale={node_locale}
                />
                {/* <TenantInfo
                    page={page}
                    homepage={homepage}
                    node_locale={page.node_locale}
                    isMobile={isMobile}
                  /> */}
              </div>
            </div>
            <div className="col-span-12 lg:col-span-6">
              <BlockWrapper>
                <div className="flex items-center justify-between mb-4">
                  <PageComponentMainHeader header={pageName} />
                </div>
                <PageComponentMainBottomBorder />
                {page?.description && (
                  <TenantDescription
                    description={page?.description}
                    node_locale={page?.node_locale}
                  />
                )}
                {homepage?.meetingPlace?.showSocialShare && (
                  <Share className="absolute -bottom-2 right-0 lg:bottom-6 lg:right-20 xl:right-24" />
                )}
              </BlockWrapper>
              {servicesSortedByPriority?.length > 0 &&
                servicesSortedByPriority?.map(
                  (service: IMeetingPlacePageServiceItem) => {
                    const serviceItemImage =
                      service?.mediaField?.jsonBynderAsset?.originalUrl ||
                      image;

                    const isSvg = serviceItemImage?.endsWith('.svg');
                    const imageSrc =
                      serviceItemImage && !isSvg
                        ? `${serviceItemImage}?fm=avif`
                        : serviceItemImage;
                    return (
                      <>
                        <BlockWrapper className="mt-6 lg:py-10 xl:py-12">
                          <div className="mb-1 flex items-center justify-between">
                            <div className="flex items-center gap-1">
                              {imageSrc && (
                                <Image
                                  src={imageSrc}
                                  alt={
                                    service?.mediaField?.altText ||
                                    page?.coverImageWrapper?.altText ||
                                    'alt text not found'
                                  }
                                  className="h-16 max-h-16 w-16 max-w-16 object-contain"
                                  width={64}
                                  height={64}
                                />
                              )}
                              <h4 className="font-title text-[18px] font-bold">
                                {service.name}
                              </h4>
                            </div>
                            <FavouriteButton
                              id={service?.sys?.id ?? ''}
                              type="service"
                              name={service?.name}
                            />
                          </div>
                          {service.description && (
                            <TenantDescription
                              description={service.description}
                              node_locale={service?.sys?.locale}
                            />
                          )}
                          {service?.floorPlanIDs?.length > 0 &&
                            homepage.meetingPlace.visioGlobeMapKey &&
                            homepage.meetingPlace.visioGlobeFloorPlanPage && (
                              <ViewOnMap
                                page={service}
                                homepage={homepage}
                                className="!mb-0 !mt-6"
                              />
                            )}
                          {isLivliFamily &&
                            service.bookingButtonText &&
                            isBookingSubdomain(service?.bookingUrl) && (
                              <Button
                                className={`mb-8 mt-4`}
                                onClick={() => {
                                  onBookingEngineButtonClick(
                                    service.bookingUrl,
                                  );
                                }}
                              >
                                {service.bookingButtonText}
                              </Button>
                            )}
                          {(!isLivliFamily ||
                            !isBookingSubdomain(service?.bookingUrl)) &&
                            service.bookingButtonText &&
                            service.bookingUrl && (
                              <LinkButton
                                to={service.bookingUrl}
                                className="mt-6"
                                openInNewTab
                                isExternalLink
                              >
                                {service.bookingButtonText}
                              </LinkButton>
                            )}
                        </BlockWrapper>
                      </>
                    );
                  },
                )}
            </div>
          </div>
        </main>
      )}
    </>
  );
};
export default MeetingPlacePageServiceType;
